import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { ExclamationIcon } from '@/components/icons/exclamation';
import { useOpening } from '@/components/pages/job-posting/use-opening';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';

import type { VonqOpeningIssue } from '../utils/vonq-posting-issues-parser';

/**
 * Human-readable error messages keyed by field error identifier.
 * These are displayed to the user without revealing any third-party integration details.
 */
const fieldErrorMessages = defineMessages({
  work_location: {
    defaultMessage: 'Work location is missing or invalid',
    description:
      'Posting issue: work location field failed validation for a channel',
  },
  country: {
    defaultMessage: 'Country is required',
    description: 'Posting issue: country field is missing for a channel',
  },
  street_address: {
    defaultMessage: 'Street address is required',
    description: 'Posting issue: street address field is missing for a channel',
  },
  city: {
    defaultMessage: 'City is required',
    description: 'Posting issue: city field is missing for a channel',
  },
  state: {
    defaultMessage: 'State is required',
    description: 'Posting issue: state field is missing for a channel',
  },
  postal_code: {
    defaultMessage: 'Postal code is required',
    description: 'Posting issue: postal code field is missing for a channel',
  },
  job_hours: {
    defaultMessage: 'Job hours are required',
    description: 'Posting issue: job hours field is missing for a channel',
  },
  years_of_experience: {
    defaultMessage: 'Years of experience is required',
    description:
      'Posting issue: years of experience field is missing for a channel',
  },
  industry: {
    defaultMessage: 'Industry is required',
    description: 'Posting issue: industry field is missing for a channel',
  },
  job_function: {
    defaultMessage: 'Job function is required',
    description: 'Posting issue: job function field is missing for a channel',
  },
  company: {
    defaultMessage: 'Company information is required',
    description:
      'Posting issue: company information field is missing for a channel',
  },
  job_title: {
    defaultMessage: 'Job title is required',
    description: 'Posting issue: job title field is missing for a channel',
  },
  job_description: {
    defaultMessage: 'Job description is required',
    description:
      'Posting issue: job description field is missing for a channel',
  },
  employment_type: {
    defaultMessage: 'Employment type is required',
    description:
      'Posting issue: employment type field is missing for a channel',
  },
  organization_name: {
    defaultMessage: 'Organization name is required',
    description:
      'Posting issue: organization name field is missing for a channel',
  },
  company_logo: {
    defaultMessage: 'Company logo is required',
    description: 'Posting issue: company logo field is missing for a channel',
  },
  working_location_address: {
    defaultMessage: 'Work address is required',
    description:
      'Posting issue: working location address field is missing for a channel',
  },
  working_location_postcode: {
    defaultMessage: 'Work location postal code is required',
    description:
      'Posting issue: working location postcode field is missing for a channel',
  },
  working_location_city: {
    defaultMessage: 'Work location city is required',
    description:
      'Posting issue: working location city field is missing for a channel',
  },
  working_location_country: {
    defaultMessage: 'Work location country is required',
    description:
      'Posting issue: working location country field is missing for a channel',
  },
  weekly_working_hours: {
    defaultMessage: 'Weekly working hours is required',
    description:
      'Posting issue: weekly working hours field is missing for a channel',
  },
  salary_period: {
    defaultMessage: 'Salary period is required',
    description: 'Posting issue: salary period field is missing for a channel',
  },
  salary_range: {
    defaultMessage: 'Salary range is required',
    description: 'Posting issue: salary range field is missing for a channel',
  },
  job_page_url: {
    defaultMessage: 'Job page URL is required',
    description: 'Posting issue: job page URL field is missing for a channel',
  },
  application_url: {
    defaultMessage: 'Application URL is required',
    description:
      'Posting issue: application URL field is missing for a channel',
  },
  target_group: {
    defaultMessage: 'Target group information is required',
    description: 'Posting issue: target group fields are missing for a channel',
  },
  recruiter_name: {
    defaultMessage: 'Recruiter name is required',
    description: 'Posting issue: recruiter name field is missing for a channel',
  },
});

interface VonqPostingIssuesProps {
  issues: VonqOpeningIssue[];
  removeOpening: (openingId: string) => void;
  allOpeningsHaveVonqPostingIssues: boolean;
}

/**
 * Displays posting validation issues grouped by opening, styled similarly
 * to the Conflicts component. Each opening shows a list of fields that need
 * to be resolved before the campaign can be posted to the channel.
 */
export function VonqPostingIssues({
  issues,
  removeOpening,
  allOpeningsHaveVonqPostingIssues,
}: VonqPostingIssuesProps) {
  if (issues.length === 0) return null;

  return (
    <div className="flex gap-2 rounded bg-rose-50 p-2">
      <ExclamationIcon className="text-fx-rose size-3.5 mt-0.5" />
      <div className="flex flex-col">
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Some openings have posting issues that need to be resolved."
            description="Banner message when channel posting validation returns field errors for one or more openings"
          />
        </Typography>

        <ul className="list-disc pl-4">
          {issues.map(issue => (
            <OpeningIssueRow
              key={issue.openingId}
              issue={issue}
              removeOpening={removeOpening}
              allOpeningsHaveVonqPostingIssues={
                allOpeningsHaveVonqPostingIssues
              }
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Renders a single opening's issues. Uses `useOpening` to resolve the
 * opening title from its ID without requiring the name to be passed through.
 */
function OpeningIssueRow({
  issue,
  removeOpening,
  allOpeningsHaveVonqPostingIssues,
}: {
  issue: VonqOpeningIssue;
  removeOpening: (openingId: string) => void;
  allOpeningsHaveVonqPostingIssues: boolean;
}) {
  const intl = useIntl();
  const { title } = useOpening(issue.openingId);

  return (
    <li>
      <Typography variant="body1">
        <span>{title}</span>{' '}
        {!allOpeningsHaveVonqPostingIssues && (
          <Button
            variant="link"
            type="button"
            onClick={() => removeOpening(issue.openingId)}
            className="!text-fx-rose-800 h-5"
          >
            <FormattedMessage
              defaultMessage="Remove"
              description="Button to remove an opening that has posting issues"
            />
          </Button>
        )}
      </Typography>

      {/* Error items use list-[circle] for an empty/hollow disc style */}
      <ul className="list-[circle] pl-4">
        {issue.errors.map(errorKey => (
          <li key={errorKey}>
            <Typography variant="body1" className="!text-fx-neutral-600">
              {intl.formatMessage(fieldErrorMessages[errorKey])}
            </Typography>
          </li>
        ))}
      </ul>
    </li>
  );
}
